import { Injectable } from '@angular/core'
import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  InteractionRequiredAuthError,
  PopupRequest,
  RedirectRequest,
  PublicClientApplication
} from '@azure/msal-browser'
import { environment } from '../../../environments/environment'

// sessionStorage key for the email/location context that must survive the full-page
// redirect to Entra and back (form state is lost across the redirect).
export const SSO_PENDING_KEY = 'sso_pending_login'

/**
 * Thin wrapper around MSAL for Entra (Azure AD) SSO. Used by the discovery-driven
 * login flow when a user resolves to the entra_sso path. This only handles the
 * Entra side (popup sign-in + token acquisition); exchanging the resulting token
 * for a SARAPlus session is done in AuthService.ssoLogin().
 */
@Injectable({ providedIn: 'root' })
export class SsoAuthService {

  #msalInstance = new PublicClientApplication(this.#buildConfiguration())
  #initPromise?: Promise<void>

  // Guarded so concurrent callers share a single initialization rather than racing two MSAL inits.
  initialize(): Promise<void> {
    return this.#initPromise ??= this.#doInitialize()
  }

  async #doInitialize(): Promise<void> {
    await this.#msalInstance.initialize()

    const account = this.#msalInstance.getActiveAccount() ?? this.#msalInstance.getAllAccounts()[0] ?? null
    if (account) {
      this.#msalInstance.setActiveAccount(account)
    }
  }

  // Starts the Entra sign-in by redirecting the whole page to the authority. When a tenantId
  // is supplied (from discovery) it overrides the default authority so users from other domains
  // are sent to their own tenant. Completion happens on return via handleRedirect().
  async signInRedirect(tenantId?: string, launchId?: string | null): Promise<void> {
    await this.initialize()
    const redirectUri = new URL('/auth/sso/callback', window.location.origin)
    if (launchId) {
      redirectUri.searchParams.set('launchId', launchId)
    }
    const request: RedirectRequest = {
      scopes: [...environment.aad.scopes],
      prompt: 'select_account',
      redirectUri: redirectUri.toString(),
      ...(tenantId ? { authority: environment.aad.authority } : {})
    }
    await this.#msalInstance.loginRedirect(request)
  }

  // Processes the redirect response when the page loads back at the redirectUri route.
  async handleRedirect(): Promise<AuthenticationResult | null> {
    await this.initialize()

    const result = await this.#msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false })
    if (result?.account) {
      this.#msalInstance.setActiveAccount(result.account)
    }
    return result
  }

  getActiveAccount(): AccountInfo | null {
    return this.#msalInstance.getActiveAccount()
  }

  async getAccessToken(): Promise<string | null> {
    await this.initialize()
    const account = this.#msalInstance.getActiveAccount() ?? this.#msalInstance.getAllAccounts()[0] ?? null
    if (!account) {
      return null
    }
    this.#msalInstance.setActiveAccount(account)
    const request: PopupRequest = { account, scopes: [...environment.aad.scopes] }
    try {
      const result = await this.#msalInstance.acquireTokenSilent(request)
      return result.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const result = await this.#msalInstance.acquireTokenPopup(request)
        return result.accessToken
      }
      return null
    }
  }

  async logout(): Promise<void> {
    await this.initialize()
    const account = this.#msalInstance.getActiveAccount()
    await this.#msalInstance.logoutPopup({
      account: account ?? undefined,
      postLogoutRedirectUri: window.location.origin
    })
  }

  #buildConfiguration(): Configuration {
    return {
      auth: {
        clientId: environment.aad.clientId,
        authority: environment.aad.authority,
        redirectUri: `${window.location.origin}/auth/sso/callback`,
        postLogoutRedirectUri: window.location.origin
      },
      cache: {
        cacheLocation: 'localStorage'
      }
    }
  }
}
